import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { SvgIconComponent } from 'angular-svg-icon';
import { TranslatePipe } from '@ngx-translate/core';
import { IDevicePicture, IDevicePictureUpdate } from '@app/api/interfaces/device-picture.interface';
import { PictureRotation } from '@app/core/enums/picture-rotation.enum';
import { SpinnerComponent } from '@app/shared/components/spinner/spinner.component';
import { PictureRotationSizeDirective } from '@app/shared/directives/picture-rotation-size.directive';
import { IPanEndEvent, IPanMoveEvent, PanDirective } from '@app/shared/directives/pan.directive';
import { FormattingDataService } from '@app/core/services/formatting-data/formatting-data.service';
import {
  COMMIT_THRESHOLD_RATIO,
  FLICK_VELOCITY,
  MIN_OPACITY,
  MIN_SCALE,
  ROTATION_SEQUENCE,
} from '@app/core/picture-gallery/configs/picture-gallery-slider.config';
import { SlideDirection } from '@app/core/picture-gallery/enums/slide-direction.enum';
import { SlideKey } from '@app/core/picture-gallery/enums/slide-key.enum';
import { ISlide } from '@app/core/picture-gallery/interfaces/picture-gallery-slide.interface';

@Component({
  selector: 'gc-picture-gallery-slider',
  standalone: true,
  imports: [
    NgClass,
    ButtonModule,
    Tooltip,
    SvgIconComponent,
    TranslatePipe,
    SpinnerComponent,
    PictureRotationSizeDirective,
    PanDirective,
  ],
  templateUrl: './picture-gallery-slider.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PictureGallerySliderComponent {
  protected readonly formattingData = inject(FormattingDataService);

  public pictures = input.required<IDevicePicture[]>();

  // Only used to seed the internal selection once (see constructor); afterwards the component lives on its own.
  public selected = input<IDevicePicture | null>(null);

  public showLoading = input<boolean>(false);

  public styleClass = input<string>('');

  public title = input<string>('');

  public rotate = output<IDevicePictureUpdate>();

  public save = output<IDevicePicture>();

  public doClose = output<void>();

  private track = viewChild<ElementRef<HTMLElement>>('track');

  // Internal, self-managed selection. Seeded once from the `selected` input, then driven by navigation only.
  private selectedPicture = signal<IDevicePicture | null>(null);

  private seeded = false;

  private pendingRotation = signal<PictureRotation | null>(null);

  // Live horizontal drag offset (px) applied on top of the centered position.
  private dragOffset = signal(0);

  // Last known track width (px); used to normalise the drag offset into slide units.
  private trackWidth = signal(0);

  // Whether the track should ease to its target (drag = off for finger-following, snap/commit = on).
  private animating = signal(false);

  // Slide the track is easing towards; committed once the transition ends.
  private pendingCommit = signal<SlideDirection | null>(null);

  public currentIndex = computed(() => this.pictures().findIndex(picture => picture.id === this.selectedPicture()?.id));

  public hasPrevious = computed(() => this.currentIndex() > 0);

  public hasNext = computed(() => this.currentIndex() < this.pictures().length - 1);

  public currentRotation = computed(
    () => this.pendingRotation() ?? this.selectedPicture()?.rotation ?? PictureRotation.Deg0
  );

  public previewPicture = computed<IDevicePicture | null>(() => {
    const selected = this.selectedPicture();

    return selected ? { ...selected, rotation: this.currentRotation() } : null;
  });

  public isDirty = computed(
    () => this.pendingRotation() !== null && this.pendingRotation() !== this.selectedPicture()?.rotation
  );

  private previousPicture = computed(() => (this.hasPrevious() ? this.pictures()[this.currentIndex() - 1] : null));

  private nextPicture = computed(() => (this.hasNext() ? this.pictures()[this.currentIndex() + 1] : null));

  // Progress of the drag in slide units: 0 = centred, +1 = fully showing previous, -1 = fully showing next.
  private dragProgress = computed(() => {
    const width = this.trackWidth();

    return width ? this.dragOffset() / width : 0;
  });

  // Always three slots (prev | current | next) so the centered slide stays at a fixed -100% offset.
  // Neighbors are rendered off-centre - shrunk and dimmed - and ease to full size as they reach the centre.
  public slides = computed<ISlide[]>(() => {
    const progress = this.dragProgress();

    return [
      this.toSlide(SlideKey.Previous, this.previousPicture(), -1, progress),
      this.toSlide(SlideKey.Current, this.previewPicture(), 0, progress),
      this.toSlide(SlideKey.Next, this.nextPicture(), 1, progress),
    ];
  });

  public trackTransform = computed(() => `translateX(calc(-100% + ${this.dragOffset()}px))`);

  public trackTransition = computed(() => (this.animating() ? 'transform 300ms ease-out' : 'none'));

  public slideTransition = computed(() =>
    this.animating() ? 'transform 300ms ease-out, opacity 300ms ease-out' : 'none'
  );

  constructor() {
    // Seed the internal selection from the input on first initialization only, then let the component live on its own.
    effect(() => {
      const selected = this.selected();

      if (!this.seeded) {
        this.seeded = true;
        this.selectedPicture.set(selected);
      }
    });

    effect(() => {
      const pictures = this.pictures();
      const current = untracked(this.selectedPicture);
      const fresh = pictures.find(picture => picture.id === current?.id);

      if (fresh && fresh !== current) {
        this.selectedPicture.set(fresh);
      }
    });

    effect(() => {
      // Re-centre instantly whenever the (internally) selected picture changes after a committed swipe.
      this.selectedPicture();

      this.pendingRotation.set(null);
      this.pendingCommit.set(null);
      this.animating.set(false);
      this.dragOffset.set(0);
    });
  }

  public onPanStart(): void {
    this.animating.set(false);
  }

  public onPanMove({ deltaX, width }: IPanMoveEvent): void {
    this.trackWidth.set(width);

    // No neighbor in the drag direction → apply resistance so it drifts to ~half and sticks.
    if (deltaX > 0 && !this.hasPrevious()) {
      this.dragOffset.set(this.resist(deltaX, width));
    } else if (deltaX < 0 && !this.hasNext()) {
      this.dragOffset.set(-this.resist(-deltaX, width));
    } else {
      this.dragOffset.set(deltaX);
    }
  }

  public onPanEnd({ deltaX, width, velocity }: IPanEndEvent): void {
    this.animating.set(true);

    const passedThreshold = Math.abs(deltaX) >= width * COMMIT_THRESHOLD_RATIO;
    const flicked = Math.abs(velocity) >= FLICK_VELOCITY;

    if ((deltaX < 0 || velocity < 0) && (passedThreshold || flicked) && this.hasNext()) {
      this.commit(SlideDirection.Next, width);
    } else if ((deltaX > 0 || velocity > 0) && (passedThreshold || flicked) && this.hasPrevious()) {
      this.commit(SlideDirection.Previous, width);
    } else {
      // Snap back to centre.
      this.dragOffset.set(0);
    }
  }

  public onTrackTransitionEnd(event: TransitionEvent): void {
    if (event.target !== event.currentTarget || event.propertyName !== 'transform') {
      return;
    }

    const commit = this.pendingCommit();

    if (commit === SlideDirection.Next) {
      this.emitNext();
    } else if (commit === SlideDirection.Previous) {
      this.emitPrevious();
    }
  }

  public onRotate(direction: 1 | -1): void {
    const nextRawIndex = ROTATION_SEQUENCE.indexOf(this.currentRotation()) + direction;
    const nextIndex = nextRawIndex < 0 ? ROTATION_SEQUENCE.length - 1 : nextRawIndex;
    const nextRotation = ROTATION_SEQUENCE[nextIndex % ROTATION_SEQUENCE.length];

    this.pendingRotation.set(nextRotation);
    this.rotate.emit({ rotation: nextRotation });
  }

  public onCancel(): void {
    this.pendingRotation.set(null);
  }

  public onSave(): void {
    const picture = this.previewPicture();

    if (!picture) {
      return;
    }

    this.save.emit(picture);
  }

  public onPrevious(): void {
    if (!this.hasPrevious()) {
      return;
    }

    this.animateToNeighbor(SlideDirection.Previous);
  }

  public onNext(): void {
    if (!this.hasNext()) {
      return;
    }

    this.animateToNeighbor(SlideDirection.Next);
  }

  public onClose(): void {
    this.doClose.emit();
  }

  private toSlide(key: SlideKey, picture: IDevicePicture | null, relativeIndex: number, progress: number): ISlide {
    // 1 when the slide sits at the centre, 0 once it is a full slide away.
    const centeredness = 1 - Math.min(Math.abs(relativeIndex + progress), 1);

    return {
      key,
      picture,
      imageUrl: picture?.imageUrl ?? '',
      rotation: picture?.rotation ?? PictureRotation.Deg0,
      date: this.formattingData.formatDateTime(picture?.modifiedAt),
      scale: MIN_SCALE + (1 - MIN_SCALE) * centeredness,
      opacity: MIN_OPACITY + (1 - MIN_OPACITY) * centeredness,
    };
  }

  // Diminishing pull that asymptotically approaches half the viewport, so it visibly "sticks".
  private resist(distance: number, width: number): number {
    const max = width / 2;

    return max * (1 - Math.exp(-distance / max));
  }

  private animateToNeighbor(direction: SlideDirection): void {
    const width = this.track()?.nativeElement.clientWidth ?? 0;

    if (!width) {
      // No geometry to animate against - fall back to an instant change.
      if (direction === SlideDirection.Next) {
        this.emitNext();
      } else {
        this.emitPrevious();
      }

      return;
    }

    this.trackWidth.set(width);
    this.animating.set(true);
    this.commit(direction, width);
  }

  private commit(direction: SlideDirection, width: number): void {
    this.pendingCommit.set(direction);
    this.dragOffset.set(direction === SlideDirection.Next ? -width : width);
  }

  private emitNext(): void {
    const index = this.currentIndex();

    if (index < this.pictures().length - 1) {
      this.selectedPicture.set(this.pictures()[index + 1]);
    }
  }

  private emitPrevious(): void {
    const index = this.currentIndex();

    if (index > 0) {
      this.selectedPicture.set(this.pictures()[index - 1]);
    }
  }
}
